import React from 'react';
import { Tooltip, Fade, useMediaQuery, useTheme } from '@mui/material';
import { lineHeight, styled } from '@mui/system';
import PropTypes from 'prop-types';

// Color definitions aligned with the design system
const colors = {
  nuetral: {
    background: '#8C8C8C',
    text: '#FFFFFF',
    border: '#8C8C8C',
  },
  main: {
    background: '#995A00',
    text: '#ffffff',
    border: '#995A00',
  },
};

// Styled tooltip component
const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ variant = 'nuetral', size = 'medium', showArrow = true }) => {
  const colorSet = colors[variant] || colors.dark;
  
  const sizeConfig = {
    small: {
      fontSize: '11px',
      lineHeight: '20px',
      padding: '4px 8px',
      borderRadius: '4px',
    //   maxWidth: '200px'
    },
    medium: {
      fontSize: '14px',
      padding: '8px 12px',
      borderRadius: '6px',
    //   maxWidth: '300px'
    },
    large: {
      fontSize: '16px',
      padding: '12px 16px',
      borderRadius: '8px',
    //   maxWidth: '400px'
    }
  };

  const currentSize = sizeConfig[size] || sizeConfig.medium;

  return {
    [`& .MuiTooltip-tooltip`]: {
      backgroundColor: colorSet.background,
      color: colorSet.text,
      fontSize: currentSize.fontSize,
      padding: currentSize.padding,
      borderRadius: currentSize.borderRadius,
      maxWidth: currentSize.maxWidth,
      border: `1px solid ${colorSet.border}`,
      boxShadow: `0 4px 12px ${colorSet.shadow}`,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      lineHeight: 1.4,
      wordWrap: 'break-word',
      position: 'relative',
      margin: showArrow ? '4px' : '0',
      
      // Ensure text is readable
      '& *': {
        color: 'inherit'
      }
    },
    
    // Arrow styling
    ...(showArrow && {
      [`& .MuiTooltip-arrow`]: {
        color: colorSet.background,
        '&::before': {
          backgroundColor: colorSet.background,
          border: `1px solid ${colorSet.border}`
        }
      }
    }),
  };
});

const CustomTooltip = ({
  children,
  title,
  variant = 'nuetral',
  size = 'small',
  placement = 'bottom',
  showArrow = true,
  enterDelay = 500,
  leaveDelay = 200,
  enterTouchDelay = 0,
  leaveTouchDelay = 2000,
  followCursor = false,
  open,
  onOpen,
  onClose,
  disableInteractive = false,
  disableFocusListener = false,
  disableHoverListener = false,
  disableTouchListener = false,
  TransitionComponent = Fade,
  TransitionProps = { timeout: 200 },
  ...rest
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Don't render tooltip if no title is provided
  if (!title) {
    return children;
  }

  // Mobile-specific adjustments
  const mobileProps = isMobile ? {
    enterTouchDelay: 0,
    leaveTouchDelay: 2000,
    enterDelay: 0,
    leaveDelay: 0,
    disableHoverListener: true, // Disable hover on mobile for better touch experience
    disableFocusListener: false,
    disableTouchListener: false,
    // Make tooltip more accessible on mobile
    disableInteractive: false,
  } : {
    enterTouchDelay,
    leaveTouchDelay,
    enterDelay,
    leaveDelay,
    disableHoverListener,
    disableFocusListener,
    disableTouchListener,
    disableInteractive,
  };

  return (
    <StyledTooltip
      title={title}
      variant={variant}
      size={size}
      showArrow={showArrow}
      placement={placement}
      arrow={showArrow}
      {...mobileProps}
      followCursor={followCursor}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      TransitionComponent={TransitionComponent}
      TransitionProps={TransitionProps}
      {...rest}
    >
      {children}
    </StyledTooltip>
  );
};

CustomTooltip.propTypes = {
  children: PropTypes.element.isRequired,
  title: PropTypes.node.isRequired,
  variant: PropTypes.oneOf([
    'nuetral',
    'main'
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  placement: PropTypes.oneOf([
    'bottom',
    'left',
    'right',
    'top'
  ]),
  showArrow: PropTypes.bool,
  enterDelay: PropTypes.number,
  leaveDelay: PropTypes.number,
  enterTouchDelay: PropTypes.number,
  leaveTouchDelay: PropTypes.number,
  followCursor: PropTypes.bool,
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  disableInteractive: PropTypes.bool,
  disableFocusListener: PropTypes.bool,
  disableHoverListener: PropTypes.bool,
  disableTouchListener: PropTypes.bool,
  TransitionComponent: PropTypes.elementType,
  TransitionProps: PropTypes.object
};

CustomTooltip.defaultProps = {
  variant: 'nuetral',
  size: 'small',
  placement: 'bottom',
  showArrow: true,
  enterDelay: 500,
  leaveDelay: 200,
  enterTouchDelay: 0,
  leaveTouchDelay: 2000,
  followCursor: false,
  disableInteractive: false,
  disableFocusListener: false,
  disableHoverListener: false,
  disableTouchListener: false,
  TransitionComponent: Fade,
  TransitionProps: { timeout: 200 }
};

export default CustomTooltip;
